import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useScreenSize } from '@/themes/media-query'
import ODataStore from 'devextreme/data/odata/store'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLoaderData } from 'react-router-typesafe'
import type {
  CellPreparedEvent,
  ColumnButtonClickEvent,
  ExportingEvent,
  RowDblClickEvent,
} from 'devextreme/ui/data_grid'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import {
  Button as GridButton,
  DataGrid,
  Column,
  ColumnChooser,
  ColumnChooserSearch,
  Editing,
  Export,
  FilterRow,
  HeaderFilter,
  Item,
  MasterDetail,
  Scrolling,
  SearchPanel,
  Sorting,
  StateStoring,
  Toolbar,
  DataGridRef,
  Search,
  FilterPanel,
  DataGridTypes,
  Pager,
} from 'devextreme-react/data-grid'
import { attivita_comm } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { useNavigate, useParams } from 'react-router-dom'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ClickEvent } from 'devextreme/ui/button'
import { confirm } from 'devextreme/ui/dialog'
import notify from 'devextreme/ui/notify'
import { activitiesPageRouteLoader } from '@/routes/activities/ActivitiesPage.route'
import { Accordion, Item as AccordionItem } from 'devextreme-react/accordion'
import { GridCellColor } from '@/enums'
import { ContentReadyEvent } from 'devextreme/ui/accordion'
import {
  createDateFilterItemDescriptor,
  FormFilterItemDescriptorType,
} from '@/components/filter-form/GenericFilterForm.types'
import GenericFilterForm from '@/components/filter-form/GenericFilterForm'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import DateColumn from '@/components/date-column/DateColumn'
import { columnSourceFactory, isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import { gridTotalItemsLabel } from '@/constants'
import DocumentHead from '@/components/document-head/DocumentHead'
import { AxiosError, AxiosResponse } from 'axios'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'

const ActivitiesPage = () => {
  const navigate = useNavigate()
  const { service, client } = useQsAdminApiManager()
  const { clientId } = useParams()
  const dataGridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()
  const { title, aziende, stati, userInfo, defaultCRUDAllowedRoles } = useLoaderData<typeof activitiesPageRouteLoader>()
  const token = useTokenRefresh()
  const now: Date = new Date()

  const getGridHeight = useCallback(() => {
    let h = '79vh'
    if (currentScreenSize.isMedium) {
      h = '75vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '75vh'
    }
    return h
  }, [currentScreenSize])

  const getFiltersConfiguration = useCallback((): FormFilterItemDescriptorType[] => {
    const fltConf: FormFilterItemDescriptorType[] = [
      {
        fieldName: 'azienda',
        placeHolder: 'Azienda',
        valueKeyName: 'id',
        valueDisplayExpr: 'nome',
        values: aziende.data.value,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['sede/azienda/id', this.currentValue]]
        },
      },
      {
        fieldName: 'stato',
        placeHolder: 'Stato',
        valueKeyName: 'id',
        valueDisplayExpr: 'nome',
        values: stati.data.value,
        composeFilterItem(): any[] | null {
          if (this.currentValue === undefined || this.currentValue === null) return null
          return [['stato/id', this.currentValue]]
        },
      },
      createDateFilterItemDescriptor('inizio', 'Data Inizio'),
    ]
    console.log('GENFLT - GETCONF :', fltConf)
    return fltConf
  }, [aziende, stati])

  const initialFilterConfiguration = useMemo(() => getFiltersConfiguration(), [getFiltersConfiguration])
  const [filterConfiguration, setFilterConfiguration] = useState(initialFilterConfiguration)
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false)
  const getCurrentFilter = useCallback((): any[] => {
    let applied: boolean = false
    const currentFlts = []
    for (const flt of filterConfiguration) {
      const fltValue = flt.composeFilterItem()
      if (fltValue !== null) {
        currentFlts.push(fltValue)
      }
      if (!applied && flt.currentValue !== flt.defaultValue) applied = true
    }
    setFiltersApplied(applied)
    console.log('GENFLT - CURRENT:', currentFlts)
    return currentFlts
  }, [filterConfiguration])

  const [currentFilter, setCurrentFilter] = useState<any[]>(clientId ? [['sede/azienda/id', Number(clientId)]] : [])

  const activitiesDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/attivita_comm`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: (e) => {
        console.error(e.errorDetails)
        if (!e.errorDetails) return
        notify(
          {
            message: `Errore : ${e.errorDetails?.message}`,
            type: 'error',
            displayTime: 5000,
          },
          {
            position: 'bottom center',
            direction: 'up-push',
          },
        )
      },
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: [
      'autore',
      'contatto_aziendale',
      'tipologia',
      'precedente',
      'stato',
      'commessa',
      'sede($expand=azienda($expand=agente($expand=commerciale_qs)))',
      'prodotto',
      'piattaforma',
    ],
    requireTotalCount: true,
    filter: currentFilter.length > 0 ? currentFilter : null,
  }

  const onExporting = (e: ExportingEvent) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('ActivitiesGrid')
    exportDataGrid({ component: e.component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Activities.xlsx')
      })
    })
  }

  const onCellPrepared = (e: CellPreparedEvent) => {
    if (e.rowType === 'data' && e.column.dataField === 'stato.nome' && e.value) {
      switch (e.value) {
        case 'COMPLETATA CON VENDITA':
        case 'COMPLETATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.EMERALD}`
          break
        }
        case 'PROGRAMMATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`
          break
        }
        case 'IN CORSO': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`
          break
        }
        case 'DA APPROVARE': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.GREY}`
          break
        }
        case 'APPROVATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.ORANGE}`
          break
        }
        case 'NON APPROVATA': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: white; background-color: ${GridCellColor.RED}`
          break
        }
      }
    }
  }

  const deleteAttivita = async (activityId: number) => {
    return await service.attivita_comm(activityId).delete()
  }

  const handleDeleteClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(dataGridRef, (rowdata: attivita_comm) => {
      const result = confirm(
        `<i>Vuoi davvero eliminare l'attività <b>${rowdata.ded_Dis}</b>?</i>`,
        'Eliminazione attività',
      )
      result.then((dialogResult) => {
        if (dialogResult === false) return
        deleteAttivita(rowdata.id)
          .then((res) => {
            dataGridRef.current?.instance().refresh()
            notify(
              {
                message: `Attività ${rowdata['ded_Dis']} eliminata con successo`,
                type: 'success',
                displayTime: 5000,
              },
              {
                position: 'bottom center',
                direction: 'up-push',
              },
            )
          })
          .catch((error) => {
            notify(
              {
                message: `Errore eliminazione attività: ${error}`,
                type: 'error',
                displayTime: 5000,
              },
              {
                position: 'bottom center',
                direction: 'up-push',
              },
            )
          })
      })
    })
  }

  const handlePromozioneClick = (e: ClickEvent) => {
    onDataGridSelectedRowButtonClick(dataGridRef, (rowdata: attivita_comm) => {
      if (rowdata.commessa) {
        notify(`Attività commerciale già collegata a commessa. Commessa: ${rowdata.commessa.ded_Dis}`, 'warning', 3000)
      } else {
        navigate({
          pathname: '/projects/new',
          search: `?activityId=${rowdata?.id}`,
        })
      }
    })
  }

  useEffect(() => {
    getCurrentFilter()
  }, [])

  return (
    <>
      {!clientId && <h2 className={'content-block'}>{title}</h2>}
      <div className={clientId ? '' : 'content-block'}>
        {!clientId && (
          <div className="accordion-generic-filter-form">
            <Accordion
              className={filtersApplied ? 'with-filter' : ''}
              collapsible={true}
              onContentReady={(e: ContentReadyEvent<any, any>) => {
                e.component?.collapseItem(0)
              }}
            >
              <AccordionItem icon="filter" title={'Filtri'}>
                <GenericFilterForm
                  FormItemDescriptors={filterConfiguration}
                  resetFilterClicked={function (e: ClickEvent): void {
                    setFilterConfiguration(getFiltersConfiguration())
                  }}
                  filterValueChanged={function (): void {
                    const currentFlts = getCurrentFilter()
                    console.log('GENFLT - VALUE CHNG', currentFlts)
                    if (JSON.stringify(currentFlts) !== JSON.stringify(currentFilter)) {
                      console.log('GENFLT - SETTING FILT', currentFlts)
                      setCurrentFilter(currentFlts)
                    }
                  }}
                />
              </AccordionItem>
            </Accordion>
          </div>
        )}
        <DataGrid
          id={clientId ? 'client-activities-grid' : 'activities-grid'}
          height={getGridHeight()}
          className={'dx-card wide-card'}
          dataSource={activitiesDataSource}
          noDataText="Nessuna attività commerciale trovata"
          remoteOperations={{
            filtering: true,
            grouping: false,
            groupPaging: false,
            paging: true,
            sorting: true,
            summary: true,
          }}
          showBorders={false}
          showColumnLines={true}
          focusedRowEnabled={true}
          defaultFocusedRowIndex={0}
          columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium}
          allowColumnResizing={true}
          allowColumnReordering={true}
          rowAlternationEnabled={true}
          wordWrapEnabled={false}
          onExporting={onExporting}
          ref={dataGridRef}
          grouping={{
            autoExpandAll: true,
          }}
          groupPanel={{
            visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
            emptyPanelText: 'Trascina qui una colonna per raggruppare',
          }}
          onCellPrepared={onCellPrepared}
          onRowDblClick={(e: RowDblClickEvent<any, any>) => {
            if (e.rowType === 'data') navigate(`${e.data.id}`)
          }}
        >
          <Pager
            visible={true}
            allowedPageSizes={'auto'}
            displayMode={'compact'}
            showPageSizeSelector={false}
            showInfo={true}
            showNavigationButtons={false}
            infoText={gridTotalItemsLabel}
          />
          <Export enabled={true} />
          <Editing allowAdding={true} />
          <HeaderFilter visible={true} />
          <FilterPanel visible={true} />
          <FilterRow visible={true} />
          <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 200 : 250} />
          <Scrolling mode="virtual" />
          <Sorting mode="multiple" />
          <ColumnChooser enabled={true} title={'Scelta Colonne'}>
            <ColumnChooserSearch enabled={true} />
          </ColumnChooser>
          <StateStoring
            enabled={true}
            type="localStorage"
            storageKey={clientId ? 'client-activities-grid' : 'activities-grid'}
            savingTimeout={50}
          />
          <MasterDetail enabled={false} />
          <Column
            visible={false}
            type="buttons"
            width={currentScreenSize.isXSmall || currentScreenSize.isSmall ? '10%' : '2%'}
            alignment="left"
            showInColumnChooser={false}
          >
            <GridButton
              hint="Details"
              icon="search"
              onClick={(e: ColumnButtonClickEvent) => {
                navigate(`${e.row?.data.id}`)
              }}
            ></GridButton>
          </Column>
          <Column
            dataField={'id'}
            caption="ID"
            defaultSortOrder="desc"
            visible={false}
            dataType="number"
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 1 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'id', true)}></HeaderFilter>
            )}
          </Column>
          <Column
            dataField="ded_Dis"
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: attivita_comm = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`${data.id}`}
                  text={data.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
                ></ObjectCodeLink>
              )
            }}
            visible={true}
            caption="CODICE"
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 100 : undefined}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 10 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'ded_Dis', true)}>
                <Search enabled={true} searchExpr={'ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          {DateColumn({
            dataField: 'inizio',
            caption: 'INIZIO',
            format: 'dd/MM/yyyy',
            width: 100,
            hidingPriority:
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 13 : undefined,
          })}
          {DateColumn({
            dataField: 'fine',
            caption: 'FINE',
            format: 'dd/MM/yyyy',
            width: 100,
            hidingPriority:
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 11 : undefined,
          })}
          <Column
            dataField={'sede.azienda.nome'}
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: attivita_comm = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/clients/${data.sede?.azienda?.id}`}
                  text={data.sede?.azienda?.nome}
                  disabled={clientId ? true : !isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
                ></ObjectCodeLink>
              )
            }}
            caption={'AZIENDA'}
            visible={clientId === undefined}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 14 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 150 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 150 : undefined}
          >
            <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'sede.azienda.nome')}>
              <Search enabled={true} searchExpr={'sede.azienda.nome'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'sede.nome'}
            caption={'SEDE'}
            allowHeaderFiltering={false}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 9 : undefined
            }
          />
          <Column
            dataField={'sede.azienda.agente.commerciale_qs.fullname'}
            caption={'AGENTE'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 3 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'agente', 'commerciale_qs.fullname')}>
                <Search enabled={true} searchExpr={'commerciale_qs.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField={'autore.fullname'}
            caption={'AUTORE'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 4 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'autore.fullname')}>
                <Search enabled={true} searchExpr={'autore.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField={'contatto_aziendale.fullname'}
            caption={'RIFERIMENTO'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 12 : undefined
            }
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 90 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 90 : undefined}
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'contatto_aziendale.fullname')}>
                <Search enabled={true} searchExpr={'contatto_aziendale.fullname'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField={'tipologia.nome'}
            caption={'TIPO'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 8 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'tipologia.nome')}>
                <Search enabled={true} searchExpr={'tipologia.nome'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField={'stato.nome'}
            caption={'STATO'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 7 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'stato.nome')}>
                <Search enabled={true} searchExpr={'stato.nome'} />
              </HeaderFilter>
            )}
          </Column>
          {/* <Column dataField={'descrizione'} caption={'DESCRIZIONE'} /> */}
          <Column
            dataField={'precedente.ded_Dis'}
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: attivita_comm = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/activities/${data.precedente?.id}`}
                  text={data.precedente?.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [...defaultCRUDAllowedRoles, Roles.Marketing])}
                ></ObjectCodeLink>
              )
            }}
            caption={'PRECEDENTE'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 6 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'precedente.ded_Dis', true)}>
                <Search enabled={true} searchExpr={'precedente.ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            dataField={'commessa.ded_Dis'}
            cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
              const data: attivita_comm = cellData.row.data
              return (
                <ObjectCodeLink
                  url={`/projects/${data.commessa?.id}`}
                  text={data.commessa?.ded_Dis}
                  disabled={!isUserRoleAllowed(userInfo?.roles, [...defaultCRUDAllowedRoles, Roles.Marketing])}
                ></ObjectCodeLink>
              )
            }}
            caption={'COMMESSA'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'attivita_comm', 'commessa.ded_Dis', true)}>
                <Search enabled={true} searchExpr={'commessa.ded_Dis'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            visible={false}
            dataField={'prodotto.nome'}
            caption={'PRODOTTO'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'qs_applicazione', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            )}
          </Column>
          <Column
            visible={false}
            dataField={'piattaforma.nome'}
            caption={'PIATTAFORMA'}
            hidingPriority={
              currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium ? 5 : undefined
            }
          >
            {!clientId && (
              <HeaderFilter dataSource={columnSourceFactory(token, 'piattaforma_attivita_lavorativa', 'nome')}>
                <Search enabled={true} searchExpr={'nome'} />
              </HeaderFilter>
            )}
          </Column>
          <Toolbar>
            <Item
              location="before"
              locateInMenu="never"
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Refresh',
                text: 'Refresh',
                icon: 'refresh',
                onClick: (e: ClickEvent) => {
                  dataGridRef.current?.instance().refresh()
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item name="groupPanel" />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              name="addRowButton"
              options={{
                hint: 'Nuova attività',
                text: 'Nuova attività',
                onClick: () => {
                  navigate(`new`)
                },
              }}
            />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.ExternalSales,
                Roles.Supervisor,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Apri riepilogo demo',
                text: 'Apri riepilogo demo',
                icon: 'group',
                onClick: async (e: ClickEvent) => {
                  dataGridRef?.current?.instance().beginCustomLoading('Esportazione riepilogo demo in corso....')
                  client
                    .get('/api/attivitacommerciale/exportdemosummary', {
                      headers: {
                        'Content-Disposition': `attachment;filename=RiepilogoDemo-${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}.xlsx`,
                        'Content-Type': 'application/octet-stream',
                      },
                      responseType: 'arraybuffer',
                    })
                    .then((response: AxiosResponse) => {
                      saveAs(
                        new Blob([response.data], { type: 'application/octet-stream' }),
                        `RiepilogoDemo-${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}.xlsx`,
                      )
                    })
                    .catch((error: AxiosError) => {
                      notify(`Errore esportazione riepilogo demo. Dettagli : ${error}`, 'error', 2000)
                    })
                    .finally(() => {
                      dataGridRef?.current?.instance().endCustomLoading()
                    })
                },
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Promuovi a commessa',
                text: 'Promuovi a commessa',
                icon: 'check',
                onClick: handlePromozioneClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={{
                hint: 'Elimina attività',
                text: 'Elimina attività',
                icon: 'trash',
                onClick: handleDeleteClick,
                stylingMode: 'text',
              }}
            ></Item>
            <Item
              name="columnChooserButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
            />
            <Item
              name="exportButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
            />
            <Item name="searchPanel" />
          </Toolbar>
        </DataGrid>
      </div>
    </>
  )
}

export default ActivitiesPage
